import { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../../../_services/apiCall';
import config from '../../../config/config';

const fetchFrameworkIds = async () => {
  try {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}getFramework`,
      {}, { type: 'ALL' }, 'GET', false, false
    );
    if (isSuccess) return (data?.data || []).map((item) => item.id);
  } catch (e) {
    // ignore — falls through to no frameworkIds
  }
  return [];
};

/**
 * Bulk-fetches reporting questions (KPI title, tags, supportedUnits, etc.)
 * for one financial year + question type, keyed by questionId. This is the
 * single source of KPI names and tags used across Evidence Library — for
 * showing a KPI name per evidence row, and for TagFilterBar-driven
 * filtering (both the listing filter and the upload modal's KPI picker
 * read the same tags off these entries, not a document's own `tags`).
 *
 * questionType: 'quantitative' | 'tabular_question' (qualitative)
 */
export const useReportingQuestionsMap = (financialYearId, questionType) => {
  const [map, setMap] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const fetch = useCallback(async () => {
    if (!financialYearId || !questionType) { setMap({}); return; }
    setIsLoading(true);
    try {
      const isQualitative = questionType === 'tabular_question';
      const frameworkIds = isQualitative ? await fetchFrameworkIds() : undefined;
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getReportingQuestionnew`,
        {},
        {
          financialYearId,
          question_type_filter: JSON.stringify([questionType]),
          ...(frameworkIds ? { frameworkIds } : {}),
          fromSettings: false,
        },
        'GET', false, false
      );
      if (isSuccess) {
        const nextMap = (data?.data || []).reduce((acc, item) => {
          acc[item.questionId] = item;
          return acc;
        }, {});
        setMap(nextMap);
      } else {
        setMap({});
      }
    } catch (e) {
      setMap({});
    } finally {
      setIsLoading(false);
    }
  }, [financialYearId, questionType]);

  useEffect(() => { fetch(); }, [fetch]);

  const options = Object.values(map).map((q) => ({
    value: q.questionId,
    label: q.title ?? q.kpi_name ?? `KPI ${q.questionId}`,
  }));

  return { map, options, isLoading, refetch: fetch };
};
